// Servicio de Configuración del Sitio
// Maneja las operaciones CRUD para la configuración general del sitio (Hero, etc.)

import { 
  getDocument, 
  updateDocument 
} from './firestore.service'
import { doc, setDoc, Timestamp } from 'firebase/firestore'
import { db } from './firebase.config'

const COLLECTION = 'siteSettings'
const DOCUMENT_ID = 'config' // Un solo documento con toda la configuración
const sanitizeEnvValue = (value) => typeof value === 'string' ? value.trim().replace(/^['"]|['"]$/g, '').trim() : value
const SYSTEM_NAME = sanitizeEnvValue(import.meta.env.VITE_SYSTEM_NAME) || 'PinoHome'

/**
 * Estructura del documento:
 * {
 *   hero: {
 *     backgroundImage: string (URL) | null,
 *     textColor: 'white' | 'black',
 *     pageTitle: string,
 *     title: string,
 *     subtitle: string,
 *     buttonText: string,
 *     buttonLink: string
 *   },
 *   updatedAt: Timestamp
 * }
 */

/**
 * Obtener la configuración del sitio
 * @returns {Promise<Object>} Configuración del sitio
 */
export const getSiteSettings = async () => {
  try {
    const settings = await getDocument(COLLECTION, DOCUMENT_ID)
    
    // Si no existe, retornar estructura por defecto
    if (!settings) {
      return {
        id: DOCUMENT_ID,
        hero: {
          backgroundImage: null,
          backgroundImages: [],
          embeddedVideos: [],
          slideDurationSeconds: 5.5,
          mediaDurations: {},
          textColor: 'white',
          pageTitle: SYSTEM_NAME,
          title: 'Transforma Tu Hogar',
          subtitle: 'Descubre muebles de diseño exclusivo con hasta 40% OFF',
          buttonText: 'Explorar Colección',
          buttonLink: '/productos'
        },
        updatedAt: null
      }
    }
    
    // Asegurar que hero existe con estructura completa
    if (!settings.hero) {
      settings.hero = {
        backgroundImage: null,
        backgroundImages: [],
        embeddedVideos: [],
        slideDurationSeconds: 5.5,
        mediaDurations: {},
        textColor: 'white',
        pageTitle: SYSTEM_NAME,
        title: 'Transforma Tu Hogar',
        subtitle: 'Descubre muebles de diseño exclusivo con hasta 40% OFF',
        buttonText: 'Explorar Colección',
        buttonLink: '/productos'
      }
    } else if (!settings.hero.pageTitle) {
      settings.hero.pageTitle = SYSTEM_NAME
    }
    const images = Array.isArray(settings.hero.backgroundImages) ? settings.hero.backgroundImages.filter(Boolean) : []
    if (images.length === 0 && settings.hero.backgroundImage) {
      settings.hero.backgroundImages = [settings.hero.backgroundImage]
    } else if (!Array.isArray(settings.hero.backgroundImages)) {
      settings.hero.backgroundImages = images
    }
    if (!Array.isArray(settings.hero.embeddedVideos)) {
      settings.hero.embeddedVideos = []
    }
    if (!settings.hero.mediaDurations || typeof settings.hero.mediaDurations !== 'object') {
      settings.hero.mediaDurations = {}
    }
    const duration = Number(settings.hero.slideDurationSeconds)
    if (!Number.isFinite(duration) || duration <= 0) {
      settings.hero.slideDurationSeconds = 5.5
    }
    
    return settings
  } catch (error) {
    console.error('Error obteniendo configuración del sitio:', error)
    throw error
  }
}

/**
 * Actualizar la configuración del sitio
 * @param {Object} settingsData - Datos de configuración
 * @returns {Promise<void>}
 */
export const updateSiteSettings = async (settingsData) => {
  try {
    // Verificar si el documento existe
    const existing = await getDocument(COLLECTION, DOCUMENT_ID)
    
    const settingsDoc = {
      ...settingsData,
      updatedAt: Timestamp.now()
    }
    
    if (existing) {
      // Actualizar documento existente
      await updateDocument(COLLECTION, DOCUMENT_ID, settingsDoc)
    } else {
      // Crear nuevo documento con ID específico usando setDoc
      settingsDoc.createdAt = Timestamp.now()
      const docRef = doc(db, COLLECTION, DOCUMENT_ID)
      await setDoc(docRef, settingsDoc)
    }
  } catch (error) {
    console.error('Error actualizando configuración del sitio:', error)
    throw error
  }
}

/**
 * Actualizar solo la configuración del Hero
 * @param {Object} heroData - Datos del Hero
 * @returns {Promise<void>}
 */
export const updateHeroSettings = async (heroData) => {
  try {
    const settings = await getSiteSettings()
    await updateSiteSettings({
      ...settings,
      hero: {
        ...settings.hero,
        ...heroData
      }
    })
  } catch (error) {
    console.error('Error actualizando configuración del Hero:', error)
    throw error
  }
}

export default {
  getSiteSettings,
  updateSiteSettings,
  updateHeroSettings
}

